import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography
} from '@mui/material';
import ConfirmationUnlinkButtonDialog from 'components/ui/feedback/ConfirmationButtonUnlink';
import { IProductWarehouseDetails } from 'features/inventory/products/types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import toast from 'shared/utils/toast';
import ProductWarehouseExam from './ProductWarehouseExam';
import ProductWarehouseToCostDialog from './ProductWarehouseToCostDialog';
import { ProductWarehouseLinkMode } from './types';
import { productWarehouseService } from '../api/product-warehouse.service';
import { EnhancedDialog } from 'components/enhanced';
import { SectionTitle } from 'components/ui/typography';

interface PreviewDialogProps {
  open: boolean;
  onClose: () => void;
  productWarehouseId: number;
  productCode: string;
  warehouseCode: string;
}

const DialogSourceCost: React.FC<PreviewDialogProps> = ({
  open,
  onClose,
  productWarehouseId,
  productCode,
  warehouseCode
}) => {
  const { t } = useTranslation(); // Assuming you're using react-i18next for translations
  const tranlatePrefix = 'entities.product_warehouse';
  const [mode, setMode] = useState<ProductWarehouseLinkMode>('');
  const [productWarehouse, setProductWarehouse] = useState<IProductWarehouseDetails | null>(null);
  const [loading, setLoading] = useState(false);

  const stopEventPropagation = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const loadProductWarehouseData = () => {
    setLoading(true);
    productWarehouseService
      .getAllBaseInformation({ id: productWarehouseId })
      .then((response) => {
        const productData = response[0];
        if (productData) {
          setProductWarehouse(productData);
          setMode('');
        }
      })
      .catch((error) => {
        toast.error(t('global.error-loading-data'));
        console.error('Error loading product warehouse:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRemoveLink = (id: number, pars: string) => {
    productWarehouseService
      .removeCostLink(+productWarehouseId, { model: pars, object_id: id })
      .then(() => {
        toast.success(t(`${tranlatePrefix}.price-origin-removed-success`));
        loadProductWarehouseData();
      });
  };

  useEffect(() => {
    if (open) {
      loadProductWarehouseData();
    }
  }, [open, productWarehouseId]);

  return (
    <EnhancedDialog
      open={open}
      onClose={onClose}
      title={t(`${tranlatePrefix}.costs-product-warehouse`, {
        product: productCode,
        warehouse: warehouseCode
      })}
      maxWidth="xl"
      costumFooter={<></>}
      sx={{ '& .MuiDialog-paper': { maxHeight: '80vh' } }}>
      {loading || !productWarehouse ? (
        <div className="w-full flex justify-center items-center p-8">
          <CircularProgress />
        </div>
      ) : (
        <div className="w-full flex flex-wrap justify-center p-2">
          <div className="w-full">
            <SectionTitle>{t('global.costs')}</SectionTitle>
          </div>
          {productWarehouse &&
            [
              { field: 'laboratories', link: '/tenant/topology/lab/' },
              { field: 'sectors', link: '/tenant/topology/sector/' },
              { field: 'operational_units', link: '/tenant/topology/operationalunit/' },
              { field: 'workstations', link: '/tenant/cost-analysis/workstations/' },
              { field: 'equipments', link: '/tenant/equipments/' }
            ].map((e: { field: ProductWarehouseLinkMode; link: string }) => (
              <div
                key={e.field}
                className="w-full sm:w-1/2 lg:w-1/5 pl-4 first:pl-0 border-r last:border-0">
                <div className="flex justify-start items-center mt-4 mb-2">
                  <Typography variant="body1" component="div" fontWeight={700}>
                    {t(`global.${e.field}`)}
                  </Typography>
                  <IconButton
                    color="primary"
                    onClick={(event) => {
                      stopEventPropagation(event);
                      setMode(e.field);
                    }}
                    onMouseDown={stopEventPropagation}>
                    <AddCircleOutlineIcon />
                  </IconButton>
                </div>
                <List dense={true} className="ml-0">
                  {productWarehouse[e.field] &&
                    productWarehouse[e.field].map((entity, i) => (
                      <ListItem
                        key={i}
                        sx={{ padding: 0 }}
                        secondaryAction={
                          <div key={i}>
                            <NavLink to={`${e.link}${entity.id}`}>
                              <IconButton edge="end">
                                <VisibilityIcon />
                              </IconButton>
                            </NavLink>
                            <ConfirmationUnlinkButtonDialog
                              iconButton={true}
                              id={entity.id}
                              pars={e.field}
                              handleUnlink={handleRemoveLink}
                            />
                          </div>
                        }>
                        <ListItemButton>
                          <ListItemText primary={entity.code} secondary={entity['description']} />
                          <Divider variant="inset" component="li" />
                        </ListItemButton>
                      </ListItem>
                    ))}
                </List>
              </div>
            ))}
          <div className="w-full py-4">
            <Card variant="outlined">
              <CardContent>
                {productWarehouse && (
                  <ProductWarehouseExam productWarehouseId={productWarehouse.id} />
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      <ProductWarehouseToCostDialog
        productWarehouseId={+productWarehouseId}
        mode={mode}
        setMode={setMode}
        callback={loadProductWarehouseData}
      />
    </EnhancedDialog>
  );
};

export default DialogSourceCost;
